import type { ReactElement } from "react";

import { useDispatch } from "react-redux";

import { Flex } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { useCompanyQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useCompanyQuota";
import { setDataCreditsModalOpen } from "app/pages/sales-intelligence/modules/quota/store";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import homePageTrackingService from "app/pages/sales-intelligence/services/tracking/home-page/trackingService";
import { isNoTouch } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import type { ITopCompanyItem } from "../../types/TopCompany";
import TopCompanyAssociatedListEmptyLink from "../TopCompanyAssociatedListLink/TopCompanyAssociatedListEmptyLink";
import TopCompanyAssociatedListLink from "../TopCompanyAssociatedListLink/TopCompanyAssociatedListLink";
import TopCompanyHeader from "../TopCompanyHeader/TopCompanyHeader";
import TopCompanyInfoItems from "../TopCompanyInfoItems/TopCompanyInfoItems";

interface IProps {
    company: ITopCompanyItem;
}

const TopCompanyGeneralInfo = ({ company }: IProps): ReactElement => {
    const dispatch = useDispatch();
    const {
        favicon,
        domain,
        companyName,
        associatedList,
        annualRevenue,
        companyCountryCode,
        industry,
        industryTaxonomy,
    } = company;

    const isDataCreditsEnabled = salesSettingsHelper.isDataCreditsEnabled;
    const isNTUser = isNoTouch(swSettings);
    const { isQuotaReached } = useCompanyQuota();

    const overviewLink = siNavigator.getCompanyAnalysisOverviewLink({
        key: domain,
        isWWW: "*",
    });

    const handleOverviewClick = (event: React.MouseEvent<HTMLSpanElement>): void => {
        if (isDataCreditsEnabled && isNTUser && isQuotaReached) {
            event.stopPropagation();
            event.preventDefault();
            dispatch(setDataCreditsModalOpen(true));
        } else {
            homePageTrackingService.trackViewCompanyAnalysis();
        }
    };

    return (
        <Flex
            gap="24px"
            flexDirection="column"
        >
            <TopCompanyHeader
                favicon={favicon}
                companyName={companyName}
                overviewLink={overviewLink}
                onOverviewClick={handleOverviewClick}
            >
                {associatedList ? (
                    <TopCompanyAssociatedListLink associatedList={associatedList} />
                ) : (
                    <TopCompanyAssociatedListEmptyLink />
                )}
            </TopCompanyHeader>

            <TopCompanyInfoItems
                annualRevenue={annualRevenue}
                companyCountryCode={companyCountryCode}
                industry={industry}
                industryTaxonomy={industryTaxonomy}
            />
        </Flex>
    );
};

export default TopCompanyGeneralInfo;
